export type ThousandsSeparator = '.' | ',' | ' ' | ''
export type DecimalSeparator = '.' | ','
export type DateFormatType = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY/MM/DD' | 'YYYY-MM-DD' | 'DD-MM-YYYY'
export type TimeFormatType = '24h' | '12h'

export interface FormatSettings {
  thousandsSeparator: ThousandsSeparator
  decimalSeparator: DecimalSeparator
  /** Solo aplica a montos monetarios (intent: 'money'). Ver FormatIntent en formatCore. */
  decimalPlaces: number
  dateFormat: DateFormatType
  timeFormat: TimeFormatType
}

/**
 * Los defaults reproducen el output actual del sistema: '.'/',' es lo que ya
 * renderizan los sitios es-ES/es-CO, y DD/MM/YYYY era el DEFAULT_DATE_FORMAT.
 * Para compañías que no toquen la configuración, el cambio es visualmente nulo.
 */
export const DEFAULT_FORMAT_SETTINGS: FormatSettings = {
  thousandsSeparator: '.',
  decimalSeparator: ',',
  decimalPlaces: 2,
  dateFormat: 'DD/MM/YYYY',
  timeFormat: '24h',
}

export const THOUSANDS_SEPARATORS: readonly ThousandsSeparator[] = ['.', ',', ' ', '']
export const DECIMAL_SEPARATORS: readonly DecimalSeparator[] = ['.', ',']
export const DATE_FORMATS: readonly DateFormatType[] = [
  'DD/MM/YYYY',
  'MM/DD/YYYY',
  'YYYY/MM/DD',
  'YYYY-MM-DD',
  'DD-MM-YYYY',
]
export const TIME_FORMATS: readonly TimeFormatType[] = ['24h', '12h']

export const MIN_DECIMAL_PLACES = 0
export const MAX_DECIMAL_PLACES = 6

/** Presets de separadores ofrecidos en la UI. El sample se calcula en vivo con el core. */
export interface NumberFormatPreset {
  id: 'dotThousands' | 'commaThousands' | 'spaceThousands' | 'none'
  thousandsSeparator: ThousandsSeparator
  decimalSeparator: DecimalSeparator
}

export const NUMBER_FORMAT_PRESETS: readonly NumberFormatPreset[] = [
  { id: 'dotThousands', thousandsSeparator: '.', decimalSeparator: ',' },
  { id: 'commaThousands', thousandsSeparator: ',', decimalSeparator: '.' },
  { id: 'spaceThousands', thousandsSeparator: ' ', decimalSeparator: ',' },
  { id: 'none', thousandsSeparator: '', decimalSeparator: '.' },
]

/**
 * Normaliza settings crudos (Firestore / localStorage / usuario) a un FormatSettings válido.
 * Cada campo cae a su default de forma independiente, así un campo corrupto no
 * descarta los demás.
 *
 * Invariante: thousandsSeparator !== decimalSeparator. Si se viola, ambos
 * separadores caen a default — un formato con separadores iguales no es parseable.
 */
export function sanitizeFormatSettings(raw: Partial<FormatSettings> | null | undefined): FormatSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_FORMAT_SETTINGS }

  let thousandsSeparator = THOUSANDS_SEPARATORS.includes(raw.thousandsSeparator as ThousandsSeparator)
    ? (raw.thousandsSeparator as ThousandsSeparator)
    : DEFAULT_FORMAT_SETTINGS.thousandsSeparator

  let decimalSeparator = DECIMAL_SEPARATORS.includes(raw.decimalSeparator as DecimalSeparator)
    ? (raw.decimalSeparator as DecimalSeparator)
    : DEFAULT_FORMAT_SETTINGS.decimalSeparator

  if (thousandsSeparator === decimalSeparator) {
    thousandsSeparator = DEFAULT_FORMAT_SETTINGS.thousandsSeparator
    decimalSeparator = DEFAULT_FORMAT_SETTINGS.decimalSeparator
  }

  const rawDecimals = raw.decimalPlaces
  const decimalPlaces =
    typeof rawDecimals === 'number' && Number.isInteger(rawDecimals) &&
    rawDecimals >= MIN_DECIMAL_PLACES && rawDecimals <= MAX_DECIMAL_PLACES
      ? rawDecimals
      : DEFAULT_FORMAT_SETTINGS.decimalPlaces

  const dateFormat = DATE_FORMATS.includes(raw.dateFormat as DateFormatType)
    ? (raw.dateFormat as DateFormatType)
    : DEFAULT_FORMAT_SETTINGS.dateFormat

  const timeFormat = TIME_FORMATS.includes(raw.timeFormat as TimeFormatType)
    ? (raw.timeFormat as TimeFormatType)
    : DEFAULT_FORMAT_SETTINGS.timeFormat

  return { thousandsSeparator, decimalSeparator, decimalPlaces, dateFormat, timeFormat }
}
